import {emptyEndereco, EnderecoVo} from './endereco-vo';
import {diffTime, toHour, toNumber} from '~utils/time-utils';
import {indexIcon, TipoEnum, zIndexStatusMonitoramento} from '~utils/zindex-status-monitoramento';
import {distinct} from '~utils/list-utils';

export interface VisitaVo {
  altoValorAgregado?: boolean;
  cargaExpressa: boolean;
  codigo: string;
  numeroOrdem: string;
  segmento?: string;
  cliente?: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  bairro: string;
  cidade: string;
  distanciaPoi: PAE<number>;
  peso?: number;
  endereco: EnderecoVo;
  volume?: number;
  valor?: number;
  cubagem?: number;
  enderecoCompleto?: string;
  enderecoCliente?: string;
  chegada: PAE<string>;
  saida: PAE<string>;
  chegadaHora?: string;
  chegadaDia?: string;
  saidaHora?: string;
  saidaDia?: string;
  horaChegadaAtual?: string;
  horaSaidaAtual?: string;
  chegadaAtualHora?: string;
  chegadaAtualDia?: string;
  saidaAtualHora?: string;
  saidaAtualDia?: string;
  chegadaDiferenca?: string;
  saidaDiferenca?: string;
  executado?: boolean;
  status: string;
  tipo?: 'TIPO_CD' | 'TIPO_PEDIDO' | 'TIPO_DEPOSITO';
  servicos: ServicoVO[];
  enderecoServico?: EnderecoVo;
  ordem: PAE<number>;
  pos?: number;
  origem?: string;
  icon?: {
    iconID?: number;
    textID?: number;
  };
  latitude?: number | null;
  longitude?: number | null;
  horaInicioNumber?: number;
  horaFimNumber?: number;
  janelaInicio?: string;
  janelaFim?: string;
  prioridadePedido?: boolean;
}

export const emptyVisita = (vo?: Partial<VisitaVo>): VisitaVo => ({
  altoValorAgregado: false,
  cargaExpressa: false,
  servicos: [],
  bairro: '', cidade: '', codigo: '', endereco: emptyEndereco(), status: '',
  ordem: emptyPAE() as unknown as PAE<number>,
  chegada: emptyPAE(),
  saida: emptyPAE(),
  distanciaPoi: emptyPAE() as unknown as PAE<number>,
  numeroOrdem: '',
  ...(vo ?? {}),
});

export interface ServicoVO {
  pos?: number;
  rotaId?: string;
  codigo?: string;
  endereco: EnderecoVo;
  enderecoCompleto: string;
  numero: string;
  documento?: string;
  notaFiscal?: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  peso: number;
  valor?: number;
  volume: number;
  cubagem: number;
  janelaInicio?: number;
  janelaFim?: number;
  tipo: TipoEnum;
  prioridade?: boolean;
}

export interface PAE<T> {
  atualizado: T;
  planejado: T;
  executado: T;
  diff?: T;
}

export const emptyServico = (): ServicoVO => ({
  endereco: emptyEndereco(),
  enderecoCompleto: '',
  numero: '',
  razaoSocial: '',
  peso: 0,
  valor: 0,
  volume: 0,
  cubagem: 0,
  tipo: TipoEnum.EC,
});

interface VisitaProcessavel {
  status: string;
  tipo?: 'TIPO_CD' | 'TIPO_PEDIDO' | 'TIPO_DEPOSITO';
  chegada: PAE<string>;
  saida: PAE<string>;
  numeroOrdem: string;
  ordem: PAE<number>;
  razaoSocial?: string;
  enderecoCompleto?: string;
  chegadaHora?: string;
  saidaHora?: string;
  horaInicioNumber?: number;
  horaFimNumber?: number;
  servicos: ServicoVO[];
  icon?: {
    iconID?: number;
    textID?: number;
  };
  janelaInicio?: string;
  janelaFim?: string;
}

export const processaVisita = (visita: VisitaProcessavel): void => {
  if (visita.chegada) {
    visita.chegada.diff = calcDiffPAE(visita.chegada);
  }
  if (visita.saida) {
    visita.saida.diff = calcDiffPAE(visita.saida);
  }
  visita.numeroOrdem = `${visita.ordem.planejado ?? '-'}`;
  visita.razaoSocial = (visita.razaoSocial ?? '-').toLowerCase();
  visita.enderecoCompleto = (visita.enderecoCompleto || '').toLowerCase();

  visita.horaInicioNumber = toNumber(visita.chegadaHora);
  visita.horaFimNumber = toNumber(visita.saidaHora);
  visita.servicos = visita.servicos ?? [];
  visita.janelaInicio = visita.servicos.map(s => toHour(s.janelaInicio || 0)).find(s => s);
  visita.janelaFim = visita.servicos.map(s => toHour(s.janelaFim || 0)).find(s => s);

  const status: string = (visita.status || '').toLowerCase();
  const iconID: number = (zIndexStatusMonitoramento[status] && zIndexStatusMonitoramento[status][0]) || 0;
  const textID = visita.tipo === 'TIPO_DEPOSITO' ? 3 : indexIcon(visita.servicos.map(s => s.tipo).filter(distinct));

  visita.icon = {iconID, textID};
};

const calcDiffPAE = (pae: PAE<string>) => diffTime(pae.planejado, pae.atualizado);

export const emptyPAE = <T>(): PAE<string> => ({atualizado: '', executado: '', planejado: ''});

export const prioridadePAE = <T>(pae: PAE<T>): T => pae.executado || pae.atualizado  || pae.planejado;
